import { Check, Clock, TriangleAlert, FlaskConical, ShieldCheck, Sparkles } from 'lucide-react';
import type { LearningSessionSummary } from '../shared/desktop-api';
import { hostHas } from '../shared/host-capabilities';

export function LearningSessionReport({
  sessions,
  failed,
  loading = false,
  compact = false,
  onRetry,
}: {
  sessions: LearningSessionSummary[];
  failed: boolean;
  loading?: boolean;
  compact?: boolean;
  onRetry: () => void;
}) {
  const latest = sessions.find((session) =>
    ['stopped', 'failed', 'interrupted'].includes(session.state),
  );
  if (loading)
    return (
      <section className="learning-session-report" aria-label="Saved learning report" role="status">
        <Clock size={26} />
        <h2>Reading the last session…</h2>
      </section>
    );
  if (failed)
    return (
      <section className="learning-session-report" aria-label="Saved learning report" role="status">
        <header>
          <strong>Saved learning report could not be read</strong>
          <button type="button" className="quiet-button small" onClick={onRetry}>
            Try reading report again
          </button>
        </header>
      </section>
    );
  // PAIGE: the empty report invites a dedicated session only where the host runs one (`training-session`).
  if (!latest)
    return compact && hostHas('training-session') ? (
      <section
        className="learning-session-report learning-report-empty"
        aria-label="Saved learning report"
      >
        <FlaskConical size={32} aria-hidden="true" />
        <span className="eyebrow">Dedicated learning</span>
        <h2>Let your reviews do more.</h2>
        <p>
          No dedicated session saved yet. A session uses your confirmations to test better ways to
          read this job.
        </p>
        <small>Ordinary review still teaches Paige between sessions.</small>
      </section>
    ) : null;
  const snapshot = latest.lastCheckpoint;
  const interrupted = latest.state === 'interrupted';
  const failedRun = latest.state === 'failed';
  const minutes = Math.max(
    0,
    Math.round(
      ((latest.endedAt ? Date.parse(latest.endedAt) : Date.parse(latest.updatedAt)) -
        Date.parse(latest.startedAt)) /
        60_000,
    ),
  );
  const changes = snapshot?.promotions.length;
  return (
    <section
      className={`learning-session-report ${compact ? 'learning-report-compact' : ''}`}
      aria-label="Saved learning report"
    >
      <header>
        <span
          className={`learning-report-symbol ${interrupted || failedRun ? 'attention' : changes ? '' : 'neutral'}`}
        >
          {interrupted || failedRun ? (
            <TriangleAlert size={25} />
          ) : changes ? (
            <ShieldCheck size={25} />
          ) : (
            <Clock size={25} />
          )}
        </span>
        <div>
          <span className="eyebrow">
            {latest.mode === 'scheduled' ? 'Last scheduled session' : 'Last dedicated session'}
          </span>
          <h2>
            {interrupted
              ? 'Interrupted - progress kept'
              : failedRun
                ? 'Stopped with an issue'
                : changes === undefined
                  ? 'Session saved · details unavailable'
                  : compact
                    ? changes
                      ? `${changes} ${changes === 1 ? 'change' : 'changes'} saved`
                      : 'No changes adopted'
                    : 'Your learning is saved'}
          </h2>
          <small>
            {new Date(latest.startedAt).toLocaleString()} ·{' '}
            {minutes < 1
              ? 'Less than a minute'
              : `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`}
            {interrupted ? ' to the last saved checkpoint' : ' in this session'}
          </small>
        </div>
      </header>
      <div className="learning-session-metrics">
        <div>
          <FlaskConical size={23} />
          <strong>{latest.tried.toLocaleString()}</strong>
          <span>variations tested</span>
        </div>
        {!compact && (
          <div>
            <Sparkles size={23} />
            <strong>{changes ?? '-'}</strong>
            <span>recorded changes saved</span>
          </div>
        )}
        <div>
          <ShieldCheck size={23} />
          <strong>{latest.refused.toLocaleString()}</strong>
          <span>candidates refused</span>
        </div>
        <div>
          <TriangleAlert size={23} />
          <strong>{latest.operationalFailures.toLocaleString()}</strong>
          <span>operational failures</span>
        </div>
      </div>
      {snapshot?.promotions.length ? (
        compact ? (
          <details className="learning-session-lessons">
            <summary>See saved changes</summary>
            <ul>
              {snapshot.promotions.map((lesson, index) => (
                <li key={index}>
                  <Check size={15} />
                  {lesson}
                </li>
              ))}
            </ul>
          </details>
        ) : (
          <div className="learning-session-lessons">
            <strong>Changes saved</strong>
            <ul>
              {snapshot.promotions.map((lesson, index) => (
                <li key={index}>
                  <Check size={15} />
                  {lesson}
                </li>
              ))}
            </ul>
          </div>
        )
      ) : (
        <p className="learning-session-outcome">
          <Clock size={15} />
          {!snapshot
            ? 'The saved checkpoint is unavailable; change details are unknown.'
            : snapshot.nothingToImprove
              ? 'More confirmations can unlock the next improvement.'
              : 'No changes adopted · Your existing rules are unchanged.'}
        </p>
      )}
      {snapshot?.refusals.length ? (
        <details>
          <summary>Why existing rules were kept</summary>
          <p>
            These comparisons did not establish a safe improvement. No decision is needed from you.
          </p>
          <ul>
            {snapshot.refusals.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {failedRun && snapshot && <p>{snapshot.narration}</p>}
      {!failedRun && snapshot?.stopReason && (
        <details>
          <summary>Why this session stopped</summary>
          <p>{snapshot.stopReason}</p>
        </details>
      )}
    </section>
  );
}
